import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$getTableCellNodeFromLexicalNode, $isTableCellNode} from '@lexical/table';
import {$isTypeaheadNode} from 'components/DSYS/editor/nodes/TypeaheadNode';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  CONTROLLED_TEXT_INSERTION_COMMAND,
  DELETE_CHARACTER_COMMAND,
  DELETE_LINE_COMMAND,
  DELETE_WORD_COMMAND,
  DROP_COMMAND,
  INSERT_LINE_BREAK_COMMAND,
  INSERT_PARAGRAPH_COMMAND,
  KEY_DOWN_COMMAND,
  LexicalEditor,
  PASTE_COMMAND,
  REMOVE_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import {useIsTemplateMode} from 'plugins/enhancedHeadings/RoleContext';
import {useEffect} from 'react';

import styles from './LockedTableCellsPlugin.module.css';

export const LockedTableCellsPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const isTemplateMode = useIsTemplateMode();

  useEffect(() => {
    if (isTemplateMode) return; // Do not lock cells in template mode
    const unregister = registerLockedTableCells(editor);
    return () => unregister();
  }, [editor, isTemplateMode]);

  return null;
};

export function registerLockedTableCells(editor: LexicalEditor) {
  const lockedCellKeys = new Set<string>();

  const isCellLocked = (cell: any) => lockedCellKeys.has(cell.getKey());

  // Only lock cell that contains a TypeaheadNode
  const unregisterUpdate = editor.registerUpdateListener(({editorState}) => {
    editorState.read(() => {
      lockedCellKeys.clear();

      const nodeMap = (editorState as unknown as {_nodeMap: Map<string, any>})._nodeMap;

      for (const node of nodeMap.values()) {
        if ($isTypeaheadNode(node)) {
          const cell = $getTableCellNodeFromLexicalNode(node);
          if ($isTableCellNode(cell)) {
            lockedCellKeys.add(cell.getKey());
          }
        }
      }
    });
  });

  function selectionInsideLockedCell(): boolean {
    const sel = $getSelection();
    if (!$isRangeSelection(sel)) return false;
    const cell = $getTableCellNodeFromLexicalNode(sel.anchor.getNode());
    return !!cell && isCellLocked(cell);
  }

  const unregisterSelection = editor.registerCommand(
    SELECTION_CHANGE_COMMAND,
    () => {
      const rootElement = editor.getRootElement();
      if (!rootElement) return false;
      if (selectionInsideLockedCell()) {
        rootElement.classList.add(styles.lockedTableCellSelection);
      } else {
        rootElement.classList.remove(styles.lockedTableCellSelection);
      }
      return false;
    },
    COMMAND_PRIORITY_HIGH
  );

  const unregisterInsertText = editor.registerCommand(
    CONTROLLED_TEXT_INSERTION_COMMAND,
    () => (selectionInsideLockedCell() ? true : false),
    COMMAND_PRIORITY_HIGH
  );

  const unregisterLineBreak = editor.registerCommand(
    INSERT_LINE_BREAK_COMMAND,
    () => (selectionInsideLockedCell() ? true : false),
    COMMAND_PRIORITY_HIGH
  );

  const unregisterParagraph = editor.registerCommand(
    INSERT_PARAGRAPH_COMMAND,
    () => (selectionInsideLockedCell() ? true : false),
    COMMAND_PRIORITY_HIGH
  );

  const unregisterPaste = editor.registerCommand(
    PASTE_COMMAND,
    () => (selectionInsideLockedCell() ? true : false),
    COMMAND_PRIORITY_HIGH
  );

  const unregisterDrop = editor.registerCommand(
    DROP_COMMAND,
    () => (selectionInsideLockedCell() ? true : false),
    COMMAND_PRIORITY_HIGH
  );

  const unregisterKeyDown = editor.registerCommand(
    KEY_DOWN_COMMAND,
    (e: KeyboardEvent) => {
      if (!selectionInsideLockedCell()) return false;
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        return true;
      }
      return false;
    },
    COMMAND_PRIORITY_HIGH
  );

  const unregisterRemoveText = editor.registerCommand(
    REMOVE_TEXT_COMMAND,
    () => (selectionInsideLockedCell() ? true : false),
    COMMAND_PRIORITY_HIGH
  );

  const unregisterDeleteCharacter = editor.registerCommand(
    DELETE_CHARACTER_COMMAND,
    () => (selectionInsideLockedCell() ? true : false),
    COMMAND_PRIORITY_HIGH
  );

  const unregisterDeleteLine = editor.registerCommand(
    DELETE_LINE_COMMAND,
    () => (selectionInsideLockedCell() ? true : false),
    COMMAND_PRIORITY_HIGH
  );

  const unregisterDeleteWord = editor.registerCommand(
    DELETE_WORD_COMMAND,
    () => (selectionInsideLockedCell() ? true : false),
    COMMAND_PRIORITY_HIGH
  );

  // Plugin cleanup
  return () => {
    unregisterSelection();
    unregisterUpdate();
    unregisterInsertText();
    unregisterRemoveText();
    unregisterDeleteCharacter();
    unregisterDeleteLine();
    unregisterDeleteWord();
    unregisterLineBreak();
    unregisterParagraph();
    unregisterKeyDown();
    unregisterPaste();
    unregisterDrop();
  };
}
